import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MemberService } from '../services/member.service';
import { EmailCategory, EmailResult, MemberEmail } from '../member-emails';

interface EmailField {
  email: string;
  category: EmailCategory;
}

interface EmailOperation {
  field: 'primary' | 'secondary';
  run: () => Promise<EmailResult<void>>;
}

/** Two email fields saved by the existing member form. */
@Component({
  selector: 'ch-member-email-settings',
  templateUrl: './member-email-settings.component.html',
  styleUrls: ['./member-email-settings.component.css'],
})
export class MemberEmailSettingsComponent implements OnChanges {
  /** Member whose existing account is edited. */
  @Input() memberId: string;
  /** Addresses included in the parent member-details response. */
  @Input() emailAddresses: MemberEmail[] = [];
  /** Disable the fields while the parent saves. */
  @Input() disabled = false;
  /** Keep the parent's legacy profile payload in sync with the saved primary. */
  @Output() primaryChanged = new EventEmitter<string>();
  /** Prevent parent saves until the current addresses have loaded. */
  @Output() unavailableChanged = new EventEmitter<boolean>();
  /** Send request failures to the parent page's existing toast mechanism. */
  @Output() requestFailed = new EventEmitter<string>();
  /** Primary input, preserving the original Email field. */
  public primary: EmailField = { email: '', category: 'UNKNOWN' };
  /** Optional second input, directly below Email. */
  public secondary: EmailField = { email: '', category: 'UNKNOWN' };
  /** Current persisted values, separate from the form draft. */
  public addresses: MemberEmail[] = [];
  /** Inline request failure. */
  public error = '';
  /** Errors attached to the input that caused the failed request. */
  public fieldErrors = { primary: '', secondary: '' };
  /** Whether authoritative values are available. */
  public loaded = false;
  /** Whether an email request is in progress. */
  public busy = false;

  constructor(private members: MemberService) {}

  /** Populate the fields from the existing member request; do not make another request. */
  public ngOnChanges(changes): void {
    if (!changes.emailAddresses) { return; }
    this.addresses = this.emailAddresses;
    this.error = '';
    this.fieldErrors = { primary: '', secondary: '' };
    const primary = this.addresses.find(address => address.slot === 'PRIMARY');
    const secondary = this.addresses.find(address => address.slot === 'SECONDARY');
    this.primary = { email: primary ? primary.email : '', category: primary ? primary.category : 'UNKNOWN' };
    this.secondary = { email: secondary ? secondary.email : '', category: secondary ? secondary.category : 'UNKNOWN' };
    this.loaded = true;
    this.setBusy(false);
  }

  /** Disable fields while loading or saving. */
  public get blocked(): boolean { return this.disabled || this.busy || !this.loaded; }
  /** A member's pending replacement must not be silently discarded by admin edits. */
  public get pending(): MemberEmail | undefined { return this.addresses.find(address => address.slot === 'PENDING'); }
  /** Place pending replacement feedback beside the address it will replace. */
  public pendingFor(field: 'primary' | 'secondary'): boolean {
    const address = this.addresses.find(item => item.slot === field.toUpperCase());
    return !!address && !!this.pending && this.pending.replacesId === address.id;
  }

  /** Identify unsaved primary edits before sending a password reset. */
  public get primaryDirty(): boolean {
    const saved = this.addresses.find(address => address.slot === 'PRIMARY');
    return !saved || this.normalized(this.primary.email) !== saved.email;
  }
  /** Show status only for the saved address, never for a different unsaved input. */
  public unverified(field: 'primary' | 'secondary'): boolean {
    const slot = field === 'primary' ? 'PRIMARY' : 'SECONDARY';
    return this.addresses.some(address => address.slot === slot && !address.verified &&
      address.email === this.normalized(this[field].email));
  }

  /** Clear stale feedback when the admin corrects an input or its category. */
  public clearFieldError(field: 'primary' | 'secondary'): void {
    this.fieldErrors[field] = '';
  }

  /** Save email drafts before the parent sends its existing profile update. */
  public async save(): Promise<boolean> {
    if (this.busy) { return false; }
    // After an ambiguous save failure, reconcile before retrying without discarding the draft.
    if (!this.loaded && !(await this.refresh())) { return false; }
    this.error = '';
    this.fieldErrors = { primary: '', secondary: '' };
    const primary = this.addresses.find(address => address.slot === 'PRIMARY');
    const secondary = this.addresses.find(address => address.slot === 'SECONDARY');
    if (!this.primary.email.trim()) {
      this.fieldErrors.primary = 'Enter an email address.';
    } else if (!this.validEmail(this.primary.email)) {
      this.fieldErrors.primary = 'Enter a valid email address.';
    }
    if (this.secondary.email.trim() && !this.validEmail(this.secondary.email)) {
      this.fieldErrors.secondary = 'Enter a valid email address.';
    }
    if (this.secondary.email.trim() && this.normalized(this.primary.email) === this.normalized(this.secondary.email)) {
      this.fieldErrors.secondary = 'Use a different address from the primary email.';
    }
    if (!primary || this.fieldErrors.primary || this.fieldErrors.secondary) { return false; }
    if (this.pending && (this.primaryDirty || this.normalized(this.secondary.email) !== (secondary ? secondary.email : ''))) {
      this.error = 'The member has a pending email replacement. Cancel it or wait for verification before changing addresses.';
      return false;
    }
    if (secondary && !this.secondary.email.trim()) {
      if (!window.confirm('Remove the secondary email? It will no longer sign in to this account.')) { return false; }
    }
    this.setBusy(true);
    const operations: EmailOperation[] = [];
    this.collectChanges(operations, primary, 'primary');
    this.collectChanges(operations, secondary, 'secondary');
    for (const operation of operations) {
      const result = await operation.run();
      if (result.ok === false) {
        if (result.kind === 'validation') {
          this.fieldErrors[operation.field] = result.message;
        } else {
          this.requestFailed.emit('Could not save the email changes. Please try again.');
        }
        // Some earlier operations may have succeeded. Keep the draft, but re-read saved values for a safe retry.
        await this.refresh();
        return false;
      }
    }
    // Even a profile-only save must use the current Primary, not the value loaded when the page opened.
    return this.refresh();
  }

  /** Cancel a pending member replacement without changing either input draft. */
  public async cancelPending(): Promise<void> {
    const pending = this.pending;
    if (!pending || this.blocked || !window.confirm('Cancel the pending email replacement?')) { return; }
    this.setBusy(true);
    const result = await this.members.removeEmail(this.memberId, pending.id).toPromise();
    this.error = '';
    if (result.ok === false) {
      if (result.kind === 'validation') {
        this.error = result.message;
      } else {
        this.requestFailed.emit('Could not cancel the email replacement. Please try again.');
      }
    }
    await this.refresh();
  }

  private collectChanges(operations: EmailOperation[], saved: MemberEmail | undefined, key: 'primary' | 'secondary'): void {
    const field = this[key];
    const email = this.normalized(field.email);
    if (!email) {
      if (saved) { operations.push({ field: key, run: () => this.members.removeEmail(this.memberId, saved.id).toPromise() }); }
    } else if (!saved || email !== saved.email) {
      operations.push({ field: key,
        run: () => this.members.addEmail(this.memberId, email, field.category, saved ? saved.id : null).toPromise() });
    } else if (field.category !== saved.category) {
      operations.push({ field: key, run: () => this.members.setEmailCategory(this.memberId, saved.id, field.category).toPromise() });
    }
  }

  private async refresh(): Promise<boolean> {
    const fields: ('primary' | 'secondary')[] = ['primary', 'secondary'];
    const unchanged = fields.map(key => {
      const saved = this.addresses.find(address => address.slot === key.toUpperCase());
      return {
        key,
        email: this.normalized(this[key].email) === (saved ? saved.email : ''),
        category: this[key].category === (saved ? saved.category : 'UNKNOWN'),
      };
    });
    this.loaded = false;
    this.setBusy(true);
    const result = await this.members.refreshMemberEmails(this.memberId).toPromise();
    if (result.ok === false) {
      this.requestFailed.emit('Could not refresh the member after saving. Please try Save again.');
      this.setBusy(false);
      return false;
    }
    this.addresses = result.value;
    // Keep real drafts on failure, but do not turn stale, untouched values into edits on the next Save.
    unchanged.forEach(field => {
      const saved = this.addresses.find(address => address.slot === field.key.toUpperCase());
      if (field.email) { this[field.key].email = saved ? saved.email : ''; }
      if (field.category) { this[field.key].category = saved ? saved.category : 'UNKNOWN'; }
    });
    const primary = this.addresses.find(address => address.slot === 'PRIMARY');
    if (primary) { this.primaryChanged.emit(primary.email); }
    this.loaded = true;
    this.setBusy(false);
    return true;
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.unavailableChanged.emit(busy);
  }

  private normalized(email: string): string { return email.trim().toLowerCase(); }
  private validEmail(email: string): boolean { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()); }
}



// WEBPACK FOOTER //
// /home/runner/_work/monorepo/monorepo/admin-portal-old/src/app/member/member-email-settings/member-email-settings.component.ts